import React, { ReactElement, useState, useEffect, useMemo } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import Head from 'next/head';
import { isEqual } from 'lodash';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { getLayout } from 'components/layouts/Header';
import OpportunityType from 'components/candidate/Onboarding/OpportunityType';
import Responsibilities from 'components/candidate/Onboarding/Responsibilities';
import Industries from 'components/candidate/Onboarding/Industries';
import Locations from 'components/candidate/Onboarding/Locations';
import RemotePreference from 'components/candidate/Onboarding/RemotePreference';
import Matches from 'components/candidate/Onboarding/Matches';
import ProgressBar from 'components/ComponentV2/ProgressBar';
import { useCandidate } from 'hooks/useCandidate';
import { useNotification } from 'hooks/useNotification';
import { withAuthCandidate } from 'lib/hocs/withAuthCandidate';
import { useCommonList } from 'lib/contexts/common-list-context';
import { AI_ONBOARDING_STEPS } from 'lib/models/candidate';
import { parseArrayResponse } from 'lib/utils/parser';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { useCandidateMatches } from 'lib/providers/CandidateMatchesProvider';

import styles from './Onboarding.module.scss';

export interface Inputs {
  interestable_id: string;
  interestable_type: string;
  weight: number;
}

const Onboarding = (): ReactElement => {
  const router = useRouter();
  const notificationInstance = useNotification();

  const { step: queryStep } = router.query;
  const [remote_opportunity, setRemoteOpportunity] = useState(false);

  const { areaOfResponsibilities, opportunityTypes, industryOptions, countries, cityCountryList } = useCommonList();

  const opportunityTypesList = useMemo(() => opportunityTypes?.filter((item) => item.display_name !== 'Events'), [opportunityTypes]);

  const { updateCandidateInfo, updateCandidateInterests, candidateResponsibilities, candidateIndustries, candidateInterestsMutate } = useCandidate();
  const { generateNewMatches, opportunityMatches, updateCandidatePreferredMatches, refetchCurrentMatches } = useCandidateMatches();

  const [step, setStep] = useState<Array<AI_ONBOARDING_STEPS>>([AI_ONBOARDING_STEPS.OPPORTUNITY_TYPE]);
  const [progressBarStep, setProgressBarStep] = useState(1);

  const [transitionState, setTransitionState] = useState<'left-to-right' | 'right-to-left'>('left-to-right');
  const [isLoading, setIsLoading] = useState(false);
  const [isBackLoading, setIsBackLoading] = useState(false);
  const [isValueEdited, setValueEdited] = useState(false);

  const { candidate } = useAuth();
  const {
    opportunity_types: candidateSelectedOpportunities,
    cities: citiesResponse,
    country_ids: candidateSelectedRemotePreference,
  } = candidate || {};

  const cities = useMemo(() => {
    return parseArrayResponse(citiesResponse);
  }, [citiesResponse]);
  const candidateLocations = useMemo(() => {
    return cities?.map((city) => city.id);
  }, [cities]);

  const candidateSelectedMatches = useMemo(() => {
    return opportunityMatches?.filter((match) => match.preferred)?.map((item) => item.id);
  }, [opportunityMatches]);

  useEffect(() => {
    if (queryStep === String(AI_ONBOARDING_STEPS.MATCHES)) {
      setIsLoading(true);
      if (remote_opportunity) {
        setStep([
          AI_ONBOARDING_STEPS.OPPORTUNITY_TYPE,
          AI_ONBOARDING_STEPS.RESPONSIBILITIES,
          AI_ONBOARDING_STEPS.INDUSTRIES,
          AI_ONBOARDING_STEPS.LOCATION,
          AI_ONBOARDING_STEPS.REMOTE_PREFERENCE,
          AI_ONBOARDING_STEPS.MATCHES,
        ]);
      } else {
        setStep([
          AI_ONBOARDING_STEPS.OPPORTUNITY_TYPE,
          AI_ONBOARDING_STEPS.RESPONSIBILITIES,
          AI_ONBOARDING_STEPS.INDUSTRIES,
          AI_ONBOARDING_STEPS.LOCATION,
          AI_ONBOARDING_STEPS.MATCHES,
        ]);
      }
      if (!opportunityMatches?.length) {
        generateNewMatches().then(() => {
          setIsLoading(false);
        });
      } else setIsLoading(false);
    }
  }, [queryStep, remote_opportunity]);

  const onOpportunityTypeSubmitClick = async ({ opportunitySelected }: { opportunitySelected: Array<string> }) => {
    try {
      setIsLoading(true);
      const moveStep = () => {
        setTransitionState('left-to-right');
        setTimeout(() => {
          setIsLoading(false);
          setStep([...step, AI_ONBOARDING_STEPS.RESPONSIBILITIES]);
          setProgressBarStep(2);
        }, 500);
      };

      const isEqualSelection = isEqual(opportunitySelected?.sort(), candidateSelectedOpportunities?.sort());
      if (!isEqualSelection) {
        setValueEdited(true);
        await updateCandidateInfo({
          partialCandidateAttributes: {
            opportunity_types: opportunitySelected,
          },
        });
      }
      moveStep();
    } catch (error) {
      setIsLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onResponsibilitiesSubmitClick = async ({
    selectedResponsibilities,
    isBackClicked,
  }: {
    selectedResponsibilities: Array<Inputs>;
    isBackClicked?: boolean;
  }) => {
    try {
      setIsLoading(true);
      const isEqualSelection = isEqual(selectedResponsibilities, candidateResponsibilities);

      const moveStep = () => {
        setTransitionState('left-to-right');
        setTimeout(() => {
          setIsLoading(false);
          setStep([...step, AI_ONBOARDING_STEPS.INDUSTRIES]);
          setProgressBarStep(3);
        }, 500);
      };

      if (!isEqualSelection) {
        setValueEdited(true);
        const interestResponse = await updateCandidateInterests({
          selectedInterests: selectedResponsibilities,
          interestable_type: 'AreaOfResponsibility',
        });
        if (interestResponse) {
          isBackClicked ? onLeftClick() : moveStep();
          candidateInterestsMutate();
        }
      } else {
        isBackClicked ? onLeftClick() : moveStep();
      }
    } catch (error) {
      setIsLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onIndustriesSubmitClick = async ({ selectedInterests, isBackClicked }: { selectedInterests: Array<Inputs>; isBackClicked?: boolean }) => {
    try {
      setIsLoading(true);
      const isEqualSelection = isEqual(selectedInterests, candidateIndustries);

      const moveStep = () => {
        setTransitionState('left-to-right');
        setTimeout(() => {
          setIsLoading(false);
          setStep([...step, AI_ONBOARDING_STEPS.LOCATION]);
          setProgressBarStep(4);
        }, 500);
      };
      if (!isEqualSelection) {
        setValueEdited(true);
        const interestResponse = await updateCandidateInterests({
          selectedInterests,
          interestable_type: 'Industry',
        });
        if (interestResponse) {
          isBackClicked ? onLeftClick() : moveStep();
          candidateInterestsMutate();
        }
      } else {
        isBackClicked ? onLeftClick() : moveStep();
      }
    } catch (error) {
      setIsLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onLocationsSubmitClick = async (isRemoteSelected: boolean, locationsSelected: Array<string>, isBackClicked?: boolean) => {
    try {
      const isEqualSelection = isEqual(locationsSelected?.sort(), candidateLocations?.sort()) && isRemoteSelected === remote_opportunity;
      setIsLoading(true);
      const moveStep = async () => {
        if (isValueEdited) {
          await generateNewMatches();
        }
        setTransitionState('left-to-right');
        setTimeout(() => {
          setIsLoading(false);
          if (isRemoteSelected) {
            setRemoteOpportunity(true);
            setStep([...step, AI_ONBOARDING_STEPS.REMOTE_PREFERENCE]);
            setProgressBarStep(5);
          } else {
            setStep([...step, AI_ONBOARDING_STEPS.MATCHES]);
            setProgressBarStep(6);
          }
        }, 500);
      };
      if (!isEqualSelection) {
        setValueEdited(true);
        const response = await updateCandidateInfo({
          partialCandidateAttributes: {
            city_ids: locationsSelected,
          },
        });

        if (response) {
          await updateCandidateInfo({
            partialCandidateAttributes: {
              onboarding_completed: true,
            },
          });
          isBackClicked ? onLeftClick() : moveStep();
        }
      } else {
        isBackClicked ? onLeftClick() : moveStep();
      }
    } catch (error) {
      setIsLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onRemotePreferenceSubmitClick = async (countrySelected: Array<string>, isBackClicked?: boolean) => {
    try {
      setIsLoading(true);
      const moveStep = () => {
        setTransitionState('left-to-right');
        setTimeout(() => {
          setIsLoading(false);
          setStep([...step, AI_ONBOARDING_STEPS.MATCHES]);
          setProgressBarStep(6);
        }, 500);
      };

      const isEqualSelection = isEqual(countrySelected?.sort(), candidateSelectedRemotePreference?.sort());
      if (!isEqualSelection) {
        setValueEdited(true);
        const response = await updateCandidateInfo({
          partialCandidateAttributes: {
            country_ids: countrySelected,
          },
        });
        if (response) {
          isBackClicked ? onLeftClick() : moveStep();
        }
      } else {
        isBackClicked ? onLeftClick() : moveStep();
      }
    } catch (error) {
      setIsLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onMatchesSubmitClick = async (selectedMatches: Array<string>, isBackClicked?: boolean) => {
    try {
      setIsLoading(true);
      const isEqualSelection = isEqual(selectedMatches?.sort(), candidateSelectedMatches?.sort());
      const moveStep = async () => {
        refetchCurrentMatches();
        setIsLoading(false);
        router.push(PAGE_ROUTES.CANDIDATE_PROFILE);
      };
      if (!isEqualSelection) {
        setValueEdited(true);
        const response = await updateCandidatePreferredMatches({
          match_ids: selectedMatches,
        });

        if (response) {
          isBackClicked ? onLeftClick() : moveStep();
        }
      } else {
        isBackClicked ? onLeftClick() : moveStep();
      }
    } catch (error) {
      setIsLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onChangePreferenceClick = () => {
    setStep([AI_ONBOARDING_STEPS.OPPORTUNITY_TYPE]);
  };

  const onLeftClick = () => {
    setIsLoading(false);
    setIsBackLoading(true);

    setTransitionState('right-to-left');
    setTimeout(() => {
      setIsBackLoading(false);
      setStep((steps) => steps.slice(0, -1));
    }, 500);
  };

  useEffect(() => {
    if (candidateSelectedRemotePreference && candidateSelectedRemotePreference?.length > 0) {
      setRemoteOpportunity(true);
    }
  }, [candidateSelectedRemotePreference]);

  const question = () => {
    const currentStep = step[step.length - 1];
    switch (currentStep) {
      case AI_ONBOARDING_STEPS.OPPORTUNITY_TYPE:
        return (
          <OpportunityType
            candidateSelectedOpportunities={candidateSelectedOpportunities || []}
            opportunityTypes={opportunityTypesList}
            onNextClick={onOpportunityTypeSubmitClick}
            isLoading={isLoading}
            isMatchingPreferences={false}
          />
        );
      case AI_ONBOARDING_STEPS.RESPONSIBILITIES:
        return (
          <Responsibilities
            candidateResponsibilities={candidateResponsibilities}
            areaOfResponsibilities={areaOfResponsibilities}
            onNextClick={onResponsibilitiesSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
          />
        );
      case AI_ONBOARDING_STEPS.INDUSTRIES:
        return (
          <Industries
            candidateIndustries={candidateIndustries}
            industries={industryOptions}
            onNextClick={onIndustriesSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isMatchingPreferences={false}
          />
        );
      case AI_ONBOARDING_STEPS.LOCATION:
        return (
          <Locations
            candidateLocations={candidateLocations}
            remote_opportunity={remote_opportunity}
            cityCountryList={cityCountryList}
            countries={countries}
            onNextClick={onLocationsSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isMatchingPreferences={false}
          />
        );
      case AI_ONBOARDING_STEPS.REMOTE_PREFERENCE:
        return (
          <RemotePreference
            candidateChoice={candidateSelectedRemotePreference as string[]}
            countries={countries}
            onNextClick={onRemotePreferenceSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isMatchingPreferences={false}
          />
        );
      case AI_ONBOARDING_STEPS.MATCHES:
        return (
          <Matches
            matchList={opportunityMatches}
            candidateSelectedMatches={candidateSelectedMatches}
            onNextClick={onMatchesSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            onChangePreferenceClick={onChangePreferenceClick}
          />
        );
      default:
        return;
    }
  };

  const totalStep = 6;
  return (
    <>
      <Head>
        <title>Onboarding | Huzzle</title>
        <meta name="description" content="Huzzle" />
      </Head>
      <section className={styles.Onboarding} data-testid="Dashboard">
        <>
          <ProgressBar
            percentCompleted={(progressBarStep / totalStep) * 100}
            color="Primary"
            showProgressWhen0={false}
            className={styles.progressBarHeader}
          />
          <SwitchTransition mode="out-in">
            <CSSTransition
              key={step[step.length - 1]}
              addEndListener={(node, done) => {
                node.addEventListener('transitionend', done, false);
              }}
              classNames={transitionState}
            >
              {question()}
            </CSSTransition>
          </SwitchTransition>
        </>
      </section>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'candidate-onboarding'])),
    },
  };
}

const OnboardingWrapper = withAuthCandidate(Onboarding);
// @ts-ignore
OnboardingWrapper.getLayout = getLayout;

export default OnboardingWrapper;
